<template>
  <v-dialog v-model="localVisible" max-width="1250px" persistent scrollable>
    <v-card>
      <v-card-title class="primary white--text">
        <v-icon dark class="mr-2">mdi-file-excel</v-icon>
        Importar capturas de Excel
        <v-spacer />
        <v-btn icon dark @click="$emit('close')"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>

      <v-card-text class="pa-6">
        <v-alert type="info" dense outlined>
          Compatible con las hojas Scores del archivo de captura y con el formato de Capturas a Reprocesar. La revisión no guarda rondas.
        </v-alert>
        <v-alert v-if="error" type="error" dense dismissible @input="error = ''">{{ error }}</v-alert>

        <v-row align="start">
          <v-col cols="12" md="8">
            <v-file-input
              v-model="file"
              accept=".xlsx,.xlsm"
              label="Archivo de capturas (.xlsx o .xlsm)"
              prepend-icon="mdi-paperclip"
              outlined dense show-size
              :disabled="loading"
              @change="clearPreview"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-btn block color="primary" outlined :disabled="!file" :loading="loading && !committing" @click="preview">
              <v-icon left>mdi-clipboard-search</v-icon>Revisar archivo
            </v-btn>
          </v-col>
        </v-row>

        <template v-if="result">
          <v-row dense class="mb-3">
            <v-col cols="6" sm="3"><v-card outlined><v-card-text class="text-center"><div class="text-h5">{{ result.summary.total }}</div><div class="text-caption">Filas</div></v-card-text></v-card></v-col>
            <v-col cols="6" sm="3"><v-card outlined color="green lighten-5"><v-card-text class="text-center"><div class="text-h5 success--text">{{ result.summary.valid }}</div><div class="text-caption">Válidas</div></v-card-text></v-card></v-col>
            <v-col cols="6" sm="3"><v-card outlined color="red lighten-5"><v-card-text class="text-center"><div class="text-h5 error--text">{{ result.summary.invalid }}</div><div class="text-caption">Con error</div></v-card-text></v-card></v-col>
            <v-col cols="6" sm="3"><v-card outlined color="orange lighten-5"><v-card-text class="text-center"><div class="text-h5 warning--text">{{ result.summary.warnings }}</div><div class="text-caption">Advertencias</div></v-card-text></v-card></v-col>
          </v-row>

          <v-alert v-if="result.summary.invalid" type="warning" dense outlined>
            Corrige todas las filas marcadas y vuelve a revisar el archivo. El lote se confirma completo o no se guarda.
          </v-alert>

          <v-data-table :headers="headers" :items="result.rows" :items-per-page="25" dense>
            <template v-slot:item.location="{ item }">{{ item.sheet }}!{{ item.excel_row }}</template>
            <template v-slot:item.round="{ item }">{{ item.holes_played }}H · {{ item.gross_score || '--' }}</template>
            <template v-slot:item.status="{ item }">
              <v-chip v-if="item.errors.length" color="error" dark x-small>Error</v-chip>
              <v-chip v-else-if="item.warnings.length" color="warning" dark x-small>Advertencia</v-chip>
              <v-chip v-else color="success" dark x-small>Lista</v-chip>
            </template>
            <template v-slot:item.messages="{ item }">
              <div v-for="message in item.errors" :key="`e-${message}`" class="error--text text-caption">{{ message }}</div>
              <div v-for="message in item.warnings" :key="`w-${message}`" class="warning--text text-caption">{{ message }}</div>
            </template>
          </v-data-table>
        </template>
      </v-card-text>

      <v-divider />
      <v-card-actions>
        <span class="text-caption grey--text">Las rondas importadas se crean como borradores para revisión de RP.</span>
        <v-spacer />
        <v-btn text @click="$emit('close')">Cerrar</v-btn>
        <v-btn
          color="success" dark
          :disabled="!canCommit"
          :loading="committing"
          @click="commit"
        ><v-icon left>mdi-check-all</v-icon>Confirmar {{ result ? result.summary.valid : 0 }} rondas</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { handicapAPI } from '@/services/handicap'

export default {
  name: 'HandicapBatchImportDialog',
  props: { visible: { type: Boolean, default: false } },
  data () {
    return {
      file: null,
      result: null,
      loading: false,
      committing: false,
      error: '',
      headers: [
        { text: 'Ubicación', value: 'location', sortable: false },
        { text: 'Clave', value: 'membership_number' },
        { text: 'Jugador', value: 'player_name' },
        { text: 'Fecha', value: 'played_on' },
        { text: 'Cancha', value: 'course_name' },
        { text: 'Salida', value: 'tee_name' },
        { text: 'Ronda / score', value: 'round', sortable: false },
        { text: 'Estado', value: 'status', sortable: false },
        { text: 'Revisión', value: 'messages', sortable: false, width: 320 }
      ]
    }
  },
  computed: {
    localVisible: {
      get () { return this.visible },
      set (value) { if (!value) this.$emit('close') }
    },
    canCommit () {
      return this.result && this.result.summary.valid > 0 && this.result.summary.invalid === 0 && !this.loading
    }
  },
  watch: {
    visible (value) { if (value) this.reset() }
  },
  methods: {
    reset () {
      this.file = null
      this.result = null
      this.loading = false
      this.committing = false
      this.error = ''
    },
    clearPreview () {
      this.result = null
      this.error = ''
    },
    apiError (error) {
      const data = error.response?.data
      if (data?.file) return Array.isArray(data.file) ? data.file.join(' ') : data.file
      if (data?.detail) return data.detail
      return 'No fue posible procesar el archivo.'
    },
    async preview () {
      if (!this.file) return
      this.loading = true
      this.error = ''
      try {
        const response = await handicapAPI.batchImport(this.file, false)
        this.result = response.data
      } catch (error) {
        this.result = null
        this.error = this.apiError(error)
      } finally {
        this.loading = false
      }
    },
    async commit () {
      if (!this.canCommit) return
      this.loading = true
      this.committing = true
      this.error = ''
      try {
        const response = await handicapAPI.batchImport(this.file, true)
        this.$emit('imported', response.data)
      } catch (error) {
        if (error.response?.data?.rows) this.result = error.response.data
        this.error = this.apiError(error)
      } finally {
        this.loading = false
        this.committing = false
      }
    }
  }
}
</script>
